import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Edit, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeManagement() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({ name: "", pin: "", email: "" });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).type !== "admin") {
      navigate("/admin");
      return;
    }
    loadEmployees();
  }, [navigate]);

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        params: { search: searchTerm || undefined }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadEmployees();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, formData);
        toast.success("Mitarbeiter aktualisiert");
      } else {
        await axios.post(`${API}/employees`, formData);
        toast.success("Mitarbeiter erstellt");
      }
      setDialogOpen(false);
      setFormData({ name: "", pin: "", email: "" });
      setEditingEmployee(null);
      loadEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      pin: employee.pin,
      email: employee.email || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diesen Mitarbeiter wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Mitarbeiter gelöscht");
      loadEmployees();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await axios.put(`${API}/employees/${employee.id}`, {
        active: !employee.active
      });
      toast.success(`Mitarbeiter ${!employee.active ? 'aktiviert' : 'deaktiviert'}`);
      loadEmployees();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/dashboard")}
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-4xl font-bold text-gray-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Mitarbeiter
            </h1>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingEmployee(null);
              setFormData({ name: "", pin: "", email: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="add-employee-button">
                <Plus className="w-4 h-4 mr-2" />
                Neuer Mitarbeiter
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-3xl">
              <DialogHeader>
                <DialogTitle>
                  {editingEmployee ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-2xl"
                    data-testid="employee-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pin">PIN-Code</Label>
                  <Input
                    id="pin"
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                    required
                    className="rounded-2xl"
                    data-testid="employee-pin-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-Mail (optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-2xl"
                    data-testid="employee-email-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="save-employee-button">
                  {editingEmployee ? "Aktualisieren" : "Erstellen"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="shadow-xl rounded-3xl border-0 mb-6">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Mitarbeiter suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-2xl"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {employees.length === 0 ? (
            <Card className="shadow-xl rounded-3xl border-0">
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">Keine Mitarbeiter gefunden</p>
              </CardContent>
            </Card>
          ) : (
            employees.map((employee) => (
              <Card key={employee.id} className="shadow-lg rounded-3xl border-0" data-testid={`employee-card-${employee.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold">{employee.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm ${employee.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {employee.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                      <p className="text-gray-600">PIN: {employee.pin}</p>
                      {employee.email && <p className="text-gray-600">{employee.email}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(employee)}
                        className="rounded-full"
                        data-testid={`toggle-active-${employee.id}`}
                      >
                        {employee.active ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(employee)}
                        className="rounded-full"
                        data-testid={`edit-${employee.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(employee.id)}
                        className="rounded-full"
                        data-testid={`delete-${employee.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
